import { Component, ElementRef, EventEmitter, HostListener, Output, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CanvasToolbarIconComponent } from '../../../icons/canvas-toolbar-icon.component';

/** Drawing tools: Circle, Rectangle, Polygon. */
export type DrawShapeMode = 'circle' | 'rectangle' | 'polygon';

@Component({
  selector: 'lib-canvas-draw-polygon-tool',
  standalone: true,
  imports: [CommonModule, CanvasToolbarIconComponent],
  template: `
    <div class="cv-tb-split cv-tb-draw-shape">
      <button type="button" class="cv-tb__btn cv-tb-split__main" (click)="onMainClick()" [title]="titleForMode(mode())">
        <lib-canvas-toolbar-icon *ngIf="mode() === 'circle'" name="circle"></lib-canvas-toolbar-icon>
        <lib-canvas-toolbar-icon *ngIf="mode() === 'rectangle'" name="rectangle"></lib-canvas-toolbar-icon>
        <lib-canvas-toolbar-icon *ngIf="mode() === 'polygon'" name="polygon"></lib-canvas-toolbar-icon>
      </button>
      <button
        type="button"
        class="cv-tb__btn cv-tb__btn--chevron-only"
        [class.cv-tb__btn--active]="isOpen()"
        (click)="toggleMenu($event)"
        title="Drawing tools"
        [attr.aria-expanded]="isOpen()"
      >
        <lib-canvas-toolbar-icon name="chevron-down" [imgClass]="chevronImgClass"></lib-canvas-toolbar-icon>
      </button>

      <div class="cv-tb-draw-shape__panel" *ngIf="isOpen()" role="menu">
        <button type="button" class="cv-tb-draw-shape__item" (click)="selectMode('circle', $event)" role="menuitem">
          <lib-canvas-toolbar-icon name="circle"></lib-canvas-toolbar-icon>
          <span>Circle</span>
        </button>
        <button type="button" class="cv-tb-draw-shape__item" (click)="selectMode('rectangle', $event)" role="menuitem">
          <lib-canvas-toolbar-icon name="rectangle"></lib-canvas-toolbar-icon>
          <span>Rectangle</span>
        </button>
        <button type="button" class="cv-tb-draw-shape__item" (click)="selectMode('polygon', $event)" role="menuitem">
          <lib-canvas-toolbar-icon name="polygon"></lib-canvas-toolbar-icon>
          <span>Polygon</span>
        </button>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class DrawPolygonToolComponent {
  @Output() draw = new EventEmitter<DrawShapeMode>();

  /** Shared class for toolbar chevrons (see `toolbar-home.component.scss`). */
  readonly chevronImgClass = 'cv-toolbar-icon-img--chevron';

  /** Default matches design: polygon / area tool shown in the toolbar. */
  readonly mode = signal<DrawShapeMode>('polygon');
  readonly isOpen = signal(false);

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  titleForMode(m: DrawShapeMode): string {
    switch (m) {
      case 'circle':
        return 'Draw circle';
      case 'rectangle':
        return 'Draw rectangle';
      default:
        return 'Draw polygon';
    }
  }

  onMainClick(): void {
    this.draw.emit(this.mode());
  }

  toggleMenu(ev: Event): void {
    ev.stopPropagation();
    this.isOpen.update((v) => !v);
  }

  selectMode(m: DrawShapeMode, ev: Event): void {
    ev.stopPropagation();
    this.mode.set(m);
    this.isOpen.set(false);
    // Selecting a mode must activate drawing on the map; otherwise prior tools (e.g. measure LineString) stay active.
    this.draw.emit(m);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.isOpen()) return;
    const t = ev.target as Node;
    if (!this.host.nativeElement.contains(t)) {
      this.isOpen.set(false);
    }
  }
}
